import React from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';

import AuthActivityTracker from './components/AuthActivityTracker';
import Header from './components/Header';
import GlobalToast from './components/GlobalToast';
import Login from './components/Login';
import GuestList from './components/GuestList';
import GuestForm from './components/GuestForm';
import GuestDetail from './components/GuestDetail';
import UserManagement from './components/UserManagement';
import ChangePassword from './components/ChangePassword';
import { APP_ROUTE_PATTERNS, APP_ROUTES } from './constants/routes';
import { USER_ROLE } from './constants/userManagement';
import { useAppSelector } from './store/hooks';
import { selectAuthUser, selectCanWrite } from './store/authSlice';
import { store } from './store/store';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0077b6',
      light: '#00b4d8',
      dark: '#023e8a',
    },
    secondary: {
      main: '#52b788',
      light: '#74c69d',
      dark: '#1b4332',
    },
    background: {
      default: '#eaf4fb',
      paper: '#ffffff',
    },
    text: {
      primary: '#023e8a',
      secondary: '#555',
    },
    error: { main: '#e63946' },
    warning: { main: '#f4a261' },
    success: { main: '#52b788' },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { boxShadow: '0 2px 16px rgba(0,119,182,0.10)' },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: '#0077b6' },
      },
    },
  },
});

// Wrapper that redirects to /login when not authenticated
const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector(selectAuthUser);
  if (!user) return <Navigate to={APP_ROUTES.LOGIN} replace />;
  return <>{children}</>;
};

// Wrapper that redirects to / when already logged in
const RequireGuest: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector(selectAuthUser);
  if (user) return <Navigate to={APP_ROUTES.HOME} replace />;
  return <>{children}</>;
};

// Wrapper that requires admin role
const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector(selectAuthUser);
  if (!user) return <Navigate to={APP_ROUTES.LOGIN} replace />;
  if (user.role !== USER_ROLE.ADMIN) return <Navigate to={APP_ROUTES.HOME} replace />;
  return <>{children}</>;
};

// Wrapper that requires write access
const RequireWrite: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector(selectAuthUser);
  const canWrite = useAppSelector(selectCanWrite);
  if (!user) return <Navigate to={APP_ROUTES.LOGIN} replace />;
  if (!canWrite) return <Navigate to={APP_ROUTES.HOME} replace />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path={APP_ROUTE_PATTERNS.LOGIN} element={<RequireGuest><Login /></RequireGuest>} />
    <Route path={APP_ROUTE_PATTERNS.HOME} element={<RequireAuth><><Header /><GuestList /></></RequireAuth>} />
    <Route path={APP_ROUTE_PATTERNS.ADD_BOOKING} element={<RequireWrite><><Header /><GuestForm /></></RequireWrite>} />
    <Route path={APP_ROUTE_PATTERNS.EDIT_BOOKING} element={<RequireWrite><><Header /><GuestForm isEdit={true} /></></RequireWrite>} />
    <Route path={APP_ROUTE_PATTERNS.VIEW_BOOKING} element={<RequireAuth><><Header /><GuestDetail /></></RequireAuth>} />
    <Route path={APP_ROUTE_PATTERNS.USER_MANAGEMENT} element={<RequireAdmin><><Header /><UserManagement /></></RequireAdmin>} />
    <Route path={APP_ROUTE_PATTERNS.CHANGE_PASSWORD} element={<RequireAuth><><Header /><ChangePassword /></></RequireAuth>} />
    <Route path={APP_ROUTE_PATTERNS.FALLBACK} element={<Navigate to={APP_ROUTES.HOME} replace />} />
  </Routes>
);

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ReduxProvider store={store}>
        <Router>
          <AppRoutes />
          <AuthActivityTracker />
          <GlobalToast />
        </Router>
      </ReduxProvider>
    </ThemeProvider>
  );
};

export default App;
